/*
  Free gift with purchase (ABD, 20/10/2026).
  snippets/fls-free-gift-abd.liquid renders what SHOULD be in the cart (data-desired) and what IS there
  (data-present). This element makes the cart match, then re-renders the drawer the same way the theme's
  gift-wrapping feature does. The drawer re-render reconnects the element, so it checks again; once the
  cart matches it does nothing, which is what stops it looping. A small attempt cap covers failures
  (e.g. a gift going out of stock mid-session).
*/
if (!customElements.get('fls-free-gift')) {
  const GIFT_PROP = '_abd_gift';
  const CHOICE_ATTR = 'Free gift choice';
  let attempts = 0;
  let busy = false;
  let pending = false;

  // A check asked for while another was running is not dropped: it runs on the newest gift block once the first finishes.
  const runPending = () => {
    if (!pending) return;
    pending = false;
    const latest = document.querySelector('fls-free-gift');
    if (latest) setTimeout(() => latest.reconcile(), 50);
  };

  customElements.define(
    'fls-free-gift',
    class FlsFreeGift extends HTMLElement {
      connectedCallback() {
        this.querySelectorAll('[data-choice]').forEach((button) => {
          button.addEventListener('click', () => this.choose(button.dataset.choice));
        });
        this.reconcile();
      }

      get desired() {
        return (this.dataset.desired || '').split(',').filter(Boolean).map(Number);
      }

      get present() {
        return (this.dataset.present || '').split(';').filter(Boolean).map((entry) => {
          const [key, variantId, quantity] = entry.split('|');
          return { key, variantId: Number(variantId), quantity: Number(quantity) };
        });
      }

      sections() {
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
        return sectionsToBundle;
      }

      async post(url, body) {
        const response = await fetch(url, { ...theme.utils.fetchConfig(), body: JSON.stringify(body) });
        return response.json();
      }

      async reconcile() {
        if (busy) {
          pending = true;
          return;
        }

        const desired = this.desired;
        const updates = {};
        const kept = new Set();
        this.present.forEach((line) => {
          if (desired.includes(line.variantId) && !kept.has(line.variantId)) {
            kept.add(line.variantId);
            if (line.quantity !== 1) updates[line.key] = 1;
          } else {
            updates[line.key] = 0;
          }
        });
        const toAdd = desired.filter((id) => !kept.has(id));

        if (Object.keys(updates).length === 0 && toAdd.length === 0) {
          attempts = 0;
          return;
        }
        if (attempts >= 3) return;
        attempts += 1;

        busy = true;
        this.classList.add('is-busy');
        try {
          if (Object.keys(updates).length) {
            await this.post(`${theme.routes.cart_update_url}`, { updates });
          }
          if (toAdd.length) {
            await this.post(`${theme.routes.cart_add_url}`, {
              items: toAdd.map((id) => ({ id, quantity: 1, properties: { [GIFT_PROP]: 'Free gift' } }))
            });
          }
          await this.refresh();
        } catch (error) {
          console.log(error);
        } finally {
          busy = false;
          this.classList.remove('is-busy');
          runPending();
        }
      }

      async choose(choice) {
        if (busy) return;
        busy = true;
        this.classList.add('is-busy');
        attempts = 0;
        try {
          await this.post(`${theme.routes.cart_update_url}`, { attributes: { [CHOICE_ATTR]: choice } });
          busy = false;
          await this.refresh();
        } catch (error) {
          console.log(error);
        } finally {
          busy = false;
          this.classList.remove('is-busy');
          runPending();
        }
      }

      async refresh() {
        // An empty update returns the cart plus the drawer sections, which the theme re-renders on cartUpdate.
        const parsedState = await this.post(`${theme.routes.cart_update_url}`, { updates: {}, sections: this.sections() });
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'fls-free-gift', cart: parsedState });
      }
    }
  );
}
